import { useEffect, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

const POLL_INTERVAL_MS = 5 * 60 * 1000;
// Practical "sticky" toast duration. The shadcn/radix toast API takes a
// finite number of milliseconds; we use a year so the toast persists until
// the user dismisses or refreshes, without bypassing the type system.
const STICKY_TOAST_MS = 365 * 24 * 60 * 60 * 1000;

type HealthShape = {
  status?: string;
  commit?: string;
  builtAt?: string;
};

function buildIdOf(health: HealthShape): string | null {
  const commit = health.commit ?? "";
  const builtAt = health.builtAt ?? "";
  if (!commit && !builtAt) return null;
  // Build identity = commit SHA + build timestamp. A redeploy that keeps
  // the commit but rebuilds (e.g. env var change, base-image refresh) still
  // shifts builtAt and must trigger the drift toast — comparing commit
  // alone would miss that case.
  if (commit === "unknown" && builtAt === "unknown") return null;
  return `${commit}:${builtAt}`;
}

async function fetchHealth(signal: AbortSignal): Promise<HealthShape | null> {
  try {
    const base = import.meta.env.BASE_URL.replace(/\/$/, "");
    const res = await fetch(`${base}/api/healthz`, {
      method: "GET",
      headers: { accept: "application/json" },
      signal,
      cache: "no-store",
    });
    if (!res.ok) return null;
    return (await res.json()) as HealthShape;
  } catch {
    return null;
  }
}

/**
 * Task #483: poll /api/healthz and surface a toast when the server
 * build identity changes underneath a long-lived browser tab. This
 * protects against version drift where a user's open SPA keeps calling
 * an API whose schema/behavior has shifted under a fresh deploy.
 *
 * The hook is fail-open: missing/unknown commit values, network errors,
 * and dev mode (where commit is "unknown" and never changes) are all
 * silent. Only a real commit-to-different-commit transition triggers
 * the toast, and only once per session.
 */
export function useVersionDriftToast() {
  const { toast } = useToast();
  const initialBuildIdRef = useRef<string | null>(null);
  const notifiedRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    const check = async () => {
      const health = await fetchHealth(controller.signal);
      if (!health) return;
      const buildId = buildIdOf(health);
      if (!buildId) return;
      if (initialBuildIdRef.current === null) {
        initialBuildIdRef.current = buildId;
        return;
      }
      if (buildId !== initialBuildIdRef.current && !notifiedRef.current) {
        notifiedRef.current = true;
        toast({
          title: "A new version is available",
          description:
            "The server was redeployed since you opened this tab. Refresh to load the latest version.",
          duration: STICKY_TOAST_MS,
        });
      }
    };

    void check();
    const interval = window.setInterval(() => {
      void check();
    }, POLL_INTERVAL_MS);

    return () => {
      controller.abort();
      window.clearInterval(interval);
    };
  }, [toast]);
}
